import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useFarms, findAnimalByChipLocal, registerBirthLocal } from "@/lib/repo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { ScanCTA } from "@/components/ScanCTA";
import { ArrowLeft, ArrowRight, MapPin, Baby, Search, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";

const last5 = (s) => (s || "").replace(/\D/g, "").slice(-5);

export default function RegisterBirth() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isHandler = user?.role === "handler";

  const [step, setStep] = useState(1);
  const allFarms = useFarms();
  const farms = useMemo(
    () => (isHandler ? allFarms.filter((f) => (user?.assigned_farm_ids || []).includes(f.id)) : allFarms),
    [allFarms, isHandler, user]
  );
  const [saving, setSaving] = useState(false);
  const [motherStatus, setMotherStatus] = useState(null); // null | "found" | "missing"
  const [motherLabel, setMotherLabel] = useState("");
  const touchedRight = useRef(false);
  const touchedLeft = useRef(false);

  const [data, setData] = useState({
    farm_id: "", birth_date: "",
    mother_chip: "", mother_lomo: "",
    // Fix K (2026-09-18): raza por defecto "Mestizo" (pedido del cliente).
    calf_chip_code: "", weight_kg: "", sex: "female", breed: "Mestizo",
    ear_tattoo_right: "", ear_tattoo_left: "", observations: "",
  });

  React.useEffect(() => {
    if (farms.length === 1 && !data.farm_id) setData((d) => ({ ...d, farm_id: farms[0].id }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farms]);

  // Auto-derivar tatuajes desde los chips (salvo que el usuario los haya editado)
  React.useEffect(() => {
    if (!touchedRight.current) setData((d) => ({ ...d, ear_tattoo_right: last5(d.calf_chip_code) }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.calf_chip_code]);
  React.useEffect(() => {
    if (!touchedLeft.current) setData((d) => ({ ...d, ear_tattoo_left: last5(d.mother_chip) }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.mother_chip]);

  const farmName = useMemo(() => farms.find((f) => f.id === data.farm_id)?.name || "", [farms, data.farm_id]);

  const findMother = async () => {
    const chip = (data.mother_chip || "").trim();
    if (!chip) return;
    const m = await findAnimalByChipLocal(chip);
    if (m) {
      setData((d) => ({ ...d, mother_lomo: d.mother_lomo || m.lomo_number || "" }));
      setMotherStatus("found");
      setMotherLabel(`Lomo ${m.lomo_number || "—"} · ${m.category || "vaca"}`);
    } else {
      setMotherStatus("missing");
      setMotherLabel("No está registrada — podés cargar el lomo a mano");
    }
  };

  const goStep2 = () => {
    if (!data.farm_id) return toast.error("Elegí el lugar de nacimiento (finca)");
    if (!data.birth_date) return toast.error("Ingresá la fecha de nacimiento");
    setStep(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!data.calf_chip_code || data.calf_chip_code.replace(/\D/g, "").length < 10)
      return toast.error("Ingresá un chip válido para el becerro (10+ dígitos)");
    setSaving(true);
    try {
      const payload = {
        farm_id: data.farm_id,
        birth_date: data.birth_date,
        mother_chip: data.mother_chip || null,
        mother_lomo: data.mother_lomo || null,
        calf_chip_code: data.calf_chip_code,
        weight_kg: data.weight_kg === "" ? null : Number(data.weight_kg),
        sex: data.sex,
        breed: data.breed || null,
        ear_tattoo_right: data.ear_tattoo_right || null,
        ear_tattoo_left: data.ear_tattoo_left || null,
        observations: data.observations || null,
      };
      const a = await registerBirthLocal(payload);
      toast.success("Nacimiento registrado");
      navigate(`/animales/${a.id}`);
    } catch (err) {
      toast.error(err?.userMessage || "No pude registrar el nacimiento");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-5" data-testid="register-birth-page">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={()=>navigate(-1)} className="h-9 w-9" data-testid="birth-back"><ArrowLeft size={18} /></Button>
        <div className="min-w-0 flex-1">
          <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-muted-foreground">Nuevo animal</div>
          <h1 className="font-editorial text-[28px] sm:text-[36px] leading-[1.05] tracking-tight">Registro de nacimiento</h1>
        </div>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-3" data-testid="birth-steps">
        <StepDot n={1} label="Lugar" active={step === 1} done={step > 1} icon={MapPin} />
        <div className={`h-px flex-1 ${step > 1 ? "bg-primary" : "bg-border"}`} />
        <StepDot n={2} label="Datos" active={step === 2} done={false} icon={Baby} />
      </div>

      {step === 1 && (
        <Card data-testid="birth-step1">
          <CardContent className="p-5 sm:p-6 space-y-4">
            <div className="text-[10.5px] font-mono uppercase tracking-[0.12em] text-muted-foreground">Paso 1 · Lugar de nacimiento</div>

            <div>
              <Label>Lugar de nacimiento (finca)</Label>
              {isHandler ? (
                <div className="mt-1 flex items-center gap-2 rounded-[calc(var(--radius)-0.35rem)] border border-border bg-accent/40 px-3 h-11" data-testid="birth-farm-locked">
                  <Lock size={14} className="text-muted-foreground" />
                  <span className="text-sm">{farmName || "Tu finca asignada"}</span>
                </div>
              ) : (
                <Select value={data.farm_id} onValueChange={(v)=>setData({ ...data, farm_id: v })}>
                  <SelectTrigger className="h-11 mt-1" data-testid="birth-farm-select"><SelectValue placeholder="Elegí la finca" /></SelectTrigger>
                  <SelectContent>
                    {farms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {isHandler ? "Solo podés reportar nacimientos en tu finca asignada." : "La finca debe estar creada previamente."}
              </p>
            </div>

            <div>
              <Label>Fecha de nacimiento</Label>
              <Input data-testid="birth-date" type="date" value={data.birth_date} onChange={(e)=>setData({ ...data, birth_date: e.target.value })} className="h-11 mt-1" />
            </div>

            <Button onClick={goStep2} className="w-full h-12" data-testid="birth-continue">
              Continuar <ArrowRight size={16} />
            </Button>
          </CardContent>
        </Card>
      )}

      {step === 2 && (
        <form onSubmit={submit} className="space-y-4" data-testid="birth-step2">
          {/* Madre */}
          <Card>
            <CardContent className="p-5 sm:p-6 space-y-4">
              <div className="text-[10.5px] font-mono uppercase tracking-[0.12em] text-muted-foreground">Madre (vaca)</div>
              <div>
                <Label>Chip de la madre</Label>
                <div className="flex gap-2 mt-1">
                  <Input data-testid="birth-mother-chip" value={data.mother_chip} onChange={(e)=>{ setMotherStatus(null); setData({ ...data, mother_chip: e.target.value.replace(/\D/g, "") }); }} className="h-11 font-mono" placeholder="990005005303968" />
                  <Button type="button" variant="outline" className="h-11 shrink-0" onClick={findMother} data-testid="birth-mother-find"><Search size={15} /></Button>
                </div>
                <div className="mt-1.5"><ScanCTA testId="birth-mother-scan" label="Escanear chip de la madre" fullWidth onChip={(c)=>{ setMotherStatus(null); setData((d)=>({ ...d, mother_chip: c })); }} /></div>
                {motherStatus === "found" && (
                  <p className="text-xs text-[hsl(var(--state-present))] mt-1.5 flex items-center gap-1" data-testid="birth-mother-found"><CheckCircle2 size={13} />{motherLabel}</p>
                )}
                {motherStatus === "missing" && (
                  <p className="text-xs text-[hsl(var(--brand-ochre))] mt-1.5" data-testid="birth-mother-missing">{motherLabel}</p>
                )}
              </div>
              <div>
                <Label>Lomo de la madre</Label>
                <Input data-testid="birth-mother-lomo" value={data.mother_lomo} onChange={(e)=>setData({ ...data, mother_lomo: e.target.value })} className="h-11 mt-1" placeholder="03968" />
              </div>
            </CardContent>
          </Card>

          {/* Becerro */}
          <Card>
            <CardContent className="p-5 sm:p-6 space-y-4">
              <div className="text-[10.5px] font-mono uppercase tracking-[0.12em] text-muted-foreground">Datos del nuevo animal (becerro)</div>
              <div>
                <Label>Chip asignado al becerro</Label>
                <Input data-testid="birth-calf-chip" required value={data.calf_chip_code} onChange={(e)=>setData({ ...data, calf_chip_code: e.target.value.replace(/\D/g, "") })} className="h-11 mt-1 font-mono" placeholder="900233000934063" />
                <div className="mt-1.5"><ScanCTA testId="birth-calf-scan" label="Escanear chip del becerro" fullWidth onChip={(c)=>setData((d)=>({ ...d, calf_chip_code: c }))} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Peso (kg)</Label>
                  <Input data-testid="birth-weight" type="number" inputMode="decimal" min={0} value={data.weight_kg} onChange={(e)=>setData({ ...data, weight_kg: e.target.value })} className="h-11 mt-1" placeholder="38" />
                </div>
                <div>
                  <Label>Sexo</Label>
                  <Select value={data.sex} onValueChange={(v)=>setData({ ...data, sex: v })}>
                    <SelectTrigger className="h-11 mt-1" data-testid="birth-sex"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="female">Hembra</SelectItem>
                      <SelectItem value="male">Macho</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Raza</Label>
                  <Input data-testid="birth-breed" value={data.breed} onChange={(e)=>setData({ ...data, breed: e.target.value })} className="h-11 mt-1" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-1">
                <div>
                  <Label className="flex items-center gap-1">Tatuaje oreja derecha</Label>
                  <Input data-testid="birth-tattoo-right" value={data.ear_tattoo_right} onChange={(e)=>{ touchedRight.current = true; setData({ ...data, ear_tattoo_right: e.target.value }); }} className="h-11 mt-1 font-mono" placeholder="últimos 5 del chip del becerro" />
                  <p className="text-[11px] text-muted-foreground mt-1">Auto: últimos 5 del chip del becerro</p>
                </div>
                <div>
                  <Label>Tatuaje oreja izquierda</Label>
                  <Input data-testid="birth-tattoo-left" value={data.ear_tattoo_left} onChange={(e)=>{ touchedLeft.current = true; setData({ ...data, ear_tattoo_left: e.target.value }); }} className="h-11 mt-1 font-mono" placeholder="últimos 5 del chip de la madre" />
                  <p className="text-[11px] text-muted-foreground mt-1">Auto: últimos 5 del chip de la madre</p>
                </div>
              </div>

              <div>
                <Label>Observaciones</Label>
                <Textarea data-testid="birth-notes" value={data.observations} onChange={(e)=>setData({ ...data, observations: e.target.value })} rows={2} className="mt-1" />
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-2">
            <Button type="button" variant="outline" className="h-12" onClick={()=>setStep(1)} data-testid="birth-prev"><ArrowLeft size={16} />Atrás</Button>
            <Button type="submit" disabled={saving} className="h-12 flex-1" data-testid="birth-submit">
              {saving ? "Registrando…" : "Registrar nacimiento"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

function StepDot({ n, label, active, done, icon: Icon }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`h-9 w-9 rounded-full grid place-items-center border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : done ? "bg-primary/10 text-primary border-primary/30" : "bg-card text-muted-foreground border-border"}`}>
        {done ? <CheckCircle2 size={16} /> : <Icon size={16} />}
      </div>
      <div className={`text-[12px] font-heading ${active || done ? "text-foreground" : "text-muted-foreground"}`}>{label}</div>
    </div>
  );
}
